import { memo } from "react";

import { Localized } from "@fluent/react";
import { Checkbox } from "@narmi/design_system";
import cc from "classcat";

import styles from "./ManageExternalAccountsPopoverContent.module.scss";

import type Account from "byzantine/src/Account";
import type { SortBy } from "../types";

export const ManageExternalAccountsPopoverContent = ({
  externalAccounts,
  currentlySortingBy,
  checkedAccountIds,
  onSortSelection,
  onAccountIdSelection,
}: {
  externalAccounts: Account[];
  currentlySortingBy: SortBy;
  checkedAccountIds: Set<string>;
  onSortSelection: (sortedBy: SortBy) => void;
  onAccountIdSelection: (accountId: string) => void;
}) => {
  const buttonStyles = cc([styles.sortOptionsRow, "button--reset"]);
  // We'll hide the sort by section if only a single balance is available.
  const hasMultipleBalancesAvailable =
    externalAccounts.filter((account) => Boolean(account.balances.available))
      .length > 1;
  return (
    <div className={styles.container} data-testid="external-accounts-popover">
      <span className={styles.heading}>
        <Localized id="external-accounts-popover-sort">Sort by</Localized>
      </span>

      <div
        className={styles.sortOptions}
        data-testid="external-accounts-sort-section"
      >
        <button
          className={buttonStyles}
          type="button"
          onClick={() => onSortSelection("alphabetically")}
          data-testid="external-accounts-sort-alphabetically"
        >
          <Localized id="external-accounts-popover-sort-alphabetically">
            Alphabetically
          </Localized>
          {currentlySortingBy === "alphabetically" ? (
            <span className="narmi-icon-check fontSize--l" />
          ) : null}
        </button>
        {hasMultipleBalancesAvailable ? (
          <button
            className={buttonStyles}
            type="button"
            onClick={() => onSortSelection("balance")}
            data-testid="external-accounts-sort-balance"
          >
            <Localized id="external-accounts-popover-sort-balance">
              Balance
            </Localized>
            {currentlySortingBy === "balance" ? (
              <span className="narmi-icon-check fontSize--l" />
            ) : null}
          </button>
        ) : null}
      </div>
      <div className={styles.divider} />
      <span className={styles.heading}>
        <Localized id="external-accounts-popover-show-or-hide">
          Show / Hide
        </Localized>
      </span>
      <div className={styles.accounts}>
        {externalAccounts.map((account) => (
          <div
            className={styles.accountsRow}
            key={account.id}
            data-testid={`external-account-checkbox-${account.id}`}
          >
            <Checkbox
              label={`${
                account.nickname || account.name
              } - ${account.number.slice(-4)}`}
              checked={checkedAccountIds.has(account.id)}
              onChange={() => onAccountIdSelection(account.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(ManageExternalAccountsPopoverContent);
